<template>
  <DashboardLayout>
    <template #header>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Modules Configuration
      </h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Enable or disable modules based on your subscription plan
      </p>
    </template>

    <div class="max-w-5xl mx-auto space-y-6">
      <!-- Plan Info -->
      <Alert variant="info">
        <template #icon>
          <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
        </template>
        <template #title>Current Plan: {{ subscription.plan.name }}</template>
        Your plan includes {{ availableModulesCount }} of {{ modules.length }} modules.
        <Link :href="route('settings.billing.index')" class="underline font-medium ml-1">
          Upgrade to unlock more modules
        </Link>
      </Alert>

      <!-- Modules List -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card
          v-for="module in modules"
          :key="module.id"
          class="hover:shadow-lg transition-shadow"
        >
          <div class="space-y-4">
            <!-- Module Header -->
            <div class="flex items-start justify-between">
              <div class="flex items-center space-x-4">
                <div
                  :class="[
                    'w-12 h-12 rounded-lg flex items-center justify-center',
                    module.color,
                  ]"
                >
                  <component :is="getModuleIcon(module.icon)" class="w-6 h-6" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {{ module.name }}
                  </h3>
                  <Badge v-if="!module.available_in_plan" variant="warning" size="xs">
                    Requires upgrade
                  </Badge>
                  <Badge v-else-if="module.enabled" variant="success" size="xs">
                    Enabled
                  </Badge>
                  <Badge v-else variant="secondary" size="xs">
                    Disabled
                  </Badge>
                </div>
              </div>
              <ToggleSwitch
                :model-value="module.enabled"
                @update:model-value="toggleModule(module)"
                :disabled="!module.available_in_plan || processing"
              />
            </div>

            <!-- Module Description -->
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ module.description }}
            </p>

            <!-- Module Features -->
            <div class="space-y-2">
              <h4 class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">
                Key Features
              </h4>
              <ul class="space-y-1">
                <li
                  v-for="(feature, index) in module.features"
                  :key="index"
                  class="flex items-center text-sm text-gray-600 dark:text-gray-400"
                >
                  <svg class="w-4 h-4 mr-2 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  {{ feature }}
                </li>
              </ul>
            </div>

            <!-- Upgrade Notice -->
            <div
              v-if="!module.available_in_plan"
              class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
            >
              <p class="text-sm text-yellow-800 dark:text-yellow-200">
                This module is not included in your current plan.
                <Link :href="route('settings.billing.index')" class="underline font-medium">
                  Upgrade now
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </div>

      <!-- Module Comparison -->
      <Card>
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Module Availability by Plan
          </h2>
        </template>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700">
                <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Module
                </th>
                <th
                  v-for="plan in plans"
                  :key="plan.id"
                  class="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  {{ plan.name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="module in modules"
                :key="module.id"
                class="border-b border-gray-200 dark:border-gray-700 last:border-0"
              >
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                  {{ module.name }}
                </td>
                <td
                  v-for="plan in plans"
                  :key="plan.id"
                  class="px-4 py-3 text-center"
                >
                  <svg
                    v-if="module.available_in_plans.includes(plan.id)"
                    class="w-5 h-5 text-green-500 mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <svg
                    v-else
                    class="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>

    <!-- Confirm Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="confirmModalTitle"
      :message="confirmModalMessage"
      :confirm-text="moduleToToggle?.enabled ? 'Disable' : 'Enable'"
      :confirm-variant="moduleToToggle?.enabled ? 'danger' : 'primary'"
      @confirm="handleConfirmToggle"
      @cancel="cancelToggle"
    />
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, h } from 'vue';
import { Link, router, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Badge from '@/Components/Common/Badge.vue';
import Alert from '@/Components/Common/Alert.vue';
import ToggleSwitch from '@/Components/Common/ToggleSwitch.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';

const props = defineProps({
  subscription: {
    type: Object,
    required: true,
  },
  modules: {
    type: Array,
    required: true,
  },
  plans: {
    type: Array,
    required: true,
  },
});

const processing = ref(false);
const showConfirmModal = ref(false);
const moduleToToggle = ref(null);

/**
 * Count of available modules in current plan
 */
const availableModulesCount = computed(() => {
  return props.modules.filter(m => m.available_in_plan).length;
});

/**
 * Modal title based on action
 */
const confirmModalTitle = computed(() => {
  if (!moduleToToggle.value) return '';
  return moduleToToggle.value.enabled
    ? `Disable ${moduleToToggle.value.name}?`
    : `Enable ${moduleToToggle.value.name}?`;
});

/**
 * Modal message based on action
 */
const confirmModalMessage = computed(() => {
  if (!moduleToToggle.value) return '';
  return moduleToToggle.value.enabled
    ? `Are you sure you want to disable the ${moduleToToggle.value.name} module? This will hide all related features.`
    : `Are you sure you want to enable the ${moduleToToggle.value.name} module? This will make all related features available.`;
});

/**
 * Get icon component for module
 */
const getModuleIcon = (iconName) => {
  const icons = {
    ticket: {
      render() {
        return h('svg', {
          xmlns: 'http://www.w3.org/2000/svg',
          fill: 'none',
          viewBox: '0 0 24 24',
          'stroke-width': '1.5',
          stroke: 'currentColor',
        }, [
          h('path', {
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            d: 'M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 010 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 010-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375z'
          })
        ]);
      },
    },
    wrench: {
      render() {
        return h('svg', {
          xmlns: 'http://www.w3.org/2000/svg',
          fill: 'none',
          viewBox: '0 0 24 24',
          'stroke-width': '1.5',
          stroke: 'currentColor',
        }, [
          h('path', {
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            d: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z'
          })
        ]);
      },
    },
    alert: {
      render() {
        return h('svg', {
          xmlns: 'http://www.w3.org/2000/svg',
          fill: 'none',
          viewBox: '0 0 24 24',
          'stroke-width': '1.5',
          stroke: 'currentColor',
        }, [
          h('path', {
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            d: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
          })
        ]);
      },
    },
  };

  return icons[iconName] || icons.ticket;
};

/**
 * Toggle module enable/disable
 */
const toggleModule = (module) => {
  if (!module.available_in_plan) {
    // Redirect to upgrade page
    router.visit(route('settings.billing.index'));
    return;
  }

  moduleToToggle.value = module;
  showConfirmModal.value = true;
};

/**
 * Confirm toggle action
 */
const handleConfirmToggle = () => {
  if (!moduleToToggle.value) return;

  processing.value = true;

  router.post(
    route('settings.modules.toggle', moduleToToggle.value.id),
    {},
    {
      preserveScroll: true,
      onSuccess: () => {
        showConfirmModal.value = false;
        moduleToToggle.value = null;
        processing.value = false;
      },
      onError: () => {
        processing.value = false;
      },
    }
  );
};

/**
 * Cancel toggle action
 */
const cancelToggle = () => {
  showConfirmModal.value = false;
  moduleToToggle.value = null;
};
</script>
